<div class="config-edit">
  <div class="edit-header">
    <a class="back-link" routerLink="/order-parser/configs">
      <i class="fas fa-arrow-left"></i> {{ 'AI_PARSER_CONFIGS_TITLE' | translate }}
    </a>
    <h3>{{ (isNew ? 'AI_PARSER_CONFIG_NEW' : 'AI_PARSER_CONFIG_EDIT') | translate }}</h3>
  </div>

  <div *ngIf="errorMessage" class="alert alert-danger">{{ errorMessage | translate: { username: responsibleUsername } }}</div>

  <div *ngIf="loading" class="edit-card">
    <span class="skeleton skeleton-field"></span>
    <span class="skeleton skeleton-field"></span>
    <span class="skeleton skeleton-field skeleton-field-tall"></span>
  </div>

  <form *ngIf="!loading && !loadFailed" class="edit-card" (ngSubmit)="save()">
    <div class="form-field">
      <label class="field-label" for="config-name">{{ 'AI_PARSER_CONFIG_NAME' | translate }}</label>
      <input
        id="config-name"
        type="text"
        class="form-control"
        [class.is-invalid]="nameError"
        [(ngModel)]="name"
        name="name"
        (ngModelChange)="nameError = ''"
        autocomplete="off" />
      <div *ngIf="nameError" class="invalid-feedback d-block">{{ nameError | translate }}</div>
    </div>

    <div class="form-field">
      <label class="field-label" for="config-instructions">{{ 'AI_PARSER_CONFIG_INSTRUCTIONS' | translate }}</label>
      <textarea
        id="config-instructions"
        class="form-control"
        rows="6"
        [(ngModel)]="aiInstructions"
        name="aiInstructions"></textarea>
      <small class="field-hint">{{ 'AI_PARSER_CONFIG_INSTRUCTIONS_HINT' | translate }}</small>

      <button
        type="button"
        class="assist-toggle"
        (click)="assistOpen = !assistOpen"
        [attr.aria-expanded]="assistOpen">
        <i class="fal" [ngClass]="assistOpen ? 'fa-chevron-up' : 'fa-wand-magic-sparkles'"></i>
        {{ 'AI_PARSER_CONFIG_ASSIST_TOGGLE' | translate }}
      </button>

      <div class="ai-assist" *ngIf="assistOpen">
        <small class="field-hint">{{ 'AI_PARSER_CONFIG_ASSIST_HINT' | translate }}</small>

        <div class="sample-row">
        <input type="file" #sampleInput hidden [accept]="sampleAccept" (change)="onSampleSelected($event)" />
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="sampleInput.click()">
          <i class="fal fa-file-arrow-up"></i> {{ 'AI_PARSER_CONFIG_ASSIST_CHOOSE_FILE' | translate }}
        </button>
        <span *ngIf="sampleFile" class="sample-name">
          {{ sampleFile.name }}
          <button type="button" class="chip-remove" (click)="clearSample()" [attr.aria-label]="'DELETE' | translate">
            <i class="fas fa-times"></i>
          </button>
        </span>
      </div>
      <div *ngIf="sampleError" class="assist-error">{{ sampleError | translate }}</div>

      <textarea
        class="form-control"
        rows="3"
        [(ngModel)]="description"
        name="assistDescription"
        [ngModelOptions]="{ standalone: true }"
        [placeholder]="'AI_PARSER_CONFIG_ASSIST_DESCRIPTION_PLACEHOLDER' | translate"></textarea>

      <div class="assist-actions">
        <button type="button" class="btn btn-outline-primary btn-sm" [disabled]="!sampleFile || generating" (click)="generate()">
          <i class="fal fa-wand-magic-sparkles" *ngIf="!generating"></i>
          <i class="fas fa-spinner fa-spin" *ngIf="generating"></i>
          {{ 'AI_PARSER_CONFIG_ASSIST_GENERATE' | translate }}
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" [disabled]="!sampleFile || !aiInstructions.trim() || testing" (click)="runTest()">
          <i class="fal fa-flask" *ngIf="!testing"></i>
          <i class="fas fa-spinner fa-spin" *ngIf="testing"></i>
          {{ 'AI_PARSER_CONFIG_ASSIST_TEST' | translate }}
        </button>
      </div>

      <div *ngIf="generateError" class="assist-error">{{ generateError | translate }}</div>
      <div *ngIf="testError" class="assist-error">{{ testError | translate }}</div>

      <div *ngIf="testResult" class="test-result">
        <div class="tr-title">{{ 'AI_PARSER_CONFIG_ASSIST_TEST_RESULT' | translate }}</div>
        <dl class="tr-fields">
          <div *ngIf="testOrderNumber">
            <dt>{{ 'AI_PARSER_ORDER_NO' | translate }}</dt>
            <dd>{{ testOrderNumber }}</dd>
          </div>
          <div *ngIf="testResult.deliveryCustomerId">
            <dt>{{ 'AI_PARSER_DELIVERY_CUSTOMER' | translate }}</dt>
            <dd>{{ testResult.deliveryCustomerId }}</dd>
          </div>
          <div *ngIf="testResult.billingCustomerId">
            <dt>{{ 'AI_PARSER_BILLING_CUSTOMER' | translate }}</dt>
            <dd>{{ testResult.billingCustomerId }}</dd>
          </div>
          <div *ngIf="testDeliveryDate">
            <dt>{{ 'AI_PARSER_DELIVERY_DATE' | translate }}</dt>
            <dd>{{ testDeliveryDate | date: 'yyyy-MM-dd' }}</dd>
          </div>
        </dl>

        <table class="tr-rows" *ngIf="testResult.rows?.length; else noRows">
          <thead>
            <tr>
              <th>#</th>
              <th>{{ 'AI_PARSER_PRODUCT' | translate }}</th>
              <th class="num">{{ 'AI_PARSER_QUANTITY' | translate }}</th>
              <th class="num">{{ 'AI_PARSER_UNIT_PRICE' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of testResult.rows; let i = index">
              <td>{{ i + 1 }}</td>
              <td>{{ row.productId }}</td>
              <td class="num">{{ row.quantity }}</td>
              <td class="num">{{ rowUnitPrice(row) | number: '1.2-2' }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #noRows>
          <div class="tr-empty">{{ 'AI_PARSER_NO_ROWS_FOUND' | translate }}</div>
        </ng-template>

        <ul class="tr-warnings" *ngIf="testWarnings.length">
          <li *ngFor="let warning of testWarnings">{{ warning.message }}</li>
        </ul>
      </div>
    </div>
    </div>

    <div class="form-field" *ngIf="users.length">
      <label class="field-label" for="responsibleUsername">{{ 'USER' | translate }}</label>
      <select
        id="responsibleUsername"
        class="form-select"
        name="responsibleUsername"
        [class.is-invalid]="responsibleUserInactive"
        [(ngModel)]="responsibleUsername"
      >
        <option value="">&mdash;</option>
        <option *ngIf="responsibleUserInactive" [value]="responsibleUsername" disabled>{{ responsibleUsername }}</option>
        <option *ngFor="let user of users" [value]="user.username">
          {{ user.name || user.username }}
        </option>
      </select>
      <div *ngIf="responsibleUserInactive" class="invalid-feedback d-block">
        {{ 'AI_PARSER_RESPONSIBLE_USER_INACTIVE' | translate: { username: responsibleUsername } }}
      </div>
    </div>

    <div class="form-field auto-submit" [class.is-locked]="!serverAutoSubmitEnabled">
      <div class="auto-submit-row">
        <input
          class="pill-switch"
          type="checkbox"
          role="switch"
          id="autoSubmitEnabled"
          name="autoSubmitEnabled"
          [disabled]="!serverAutoSubmitEnabled"
          [(ngModel)]="autoSubmitEnabled"
        />
        <label class="auto-submit-label" for="autoSubmitEnabled">
          {{ 'AI_PARSER_CONFIG_AUTO_SUBMIT_LABEL' | translate }}
        </label>
      </div>
      <small class="field-hint auto-submit-help">{{ 'AI_PARSER_CONFIG_AUTO_SUBMIT_HINT' | translate }}</small>
      <small class="auto-submit-locked" *ngIf="!serverAutoSubmitEnabled">
        <i class="fal fa-lock"></i>
        <span>
          {{ 'AI_PARSER_CONFIG_AUTO_SUBMIT_SERVER_OFF' | translate }}
          <a routerLink="/order-parser/settings">{{ 'AI_PARSER_CONFIG_AUTO_SUBMIT_OPEN_SETTINGS' | translate }}</a>
        </span>
      </small>
    </div>

    <div class="form-field">
      <label class="field-label">{{ 'AI_PARSER_CONFIG_SENDER_EMAILS' | translate }}</label>
      <small class="field-hint">{{ 'AI_PARSER_CONFIG_SENDER_EMAILS_HINT' | translate }}</small>
      <div class="email-chips" *ngIf="senderEmails.length">
        <span class="email-chip" *ngFor="let email of senderEmails">
          {{ email }}
          <button type="button" class="chip-remove" (click)="removeEmail(email)" [attr.aria-label]="'DELETE' | translate">
            <i class="fas fa-times"></i>
          </button>
        </span>
      </div>
      <div class="email-add">
        <input
          type="text"
          class="form-control"
          [(ngModel)]="newEmail"
          name="newEmail"
          [placeholder]="'AI_PARSER_CONFIG_SENDER_EMAIL_PLACEHOLDER' | translate"
          (keydown.enter)="$event.preventDefault(); addEmail()"
          autocomplete="off" />
        <button type="button" class="btn btn-link text-button" (click)="addEmail()">
          <i class="fal fa-plus"></i> {{ 'ADD' | translate }}
        </button>
      </div>
    </div>

    <div class="customer-grid">
      <div class="form-field">
        <label class="field-label" for="config-delivery">{{ 'AI_PARSER_CONFIG_DELIVERY_CUSTOMER' | translate }}</label>
        <input
          id="config-delivery"
          type="text"
          class="form-control"
          [(ngModel)]="deliveryCustomerId"
          name="deliveryCustomerId"
          (ngModelChange)="onCustomerIdChange('delivery')"
          [ngbTypeahead]="searchCustomersByName"
          [resultFormatter]="customerSearchFormatter"
          [inputFormatter]="customerIdFormatter"
          (selectItem)="onCustomerPicked('delivery', $event)"
          autocomplete="off" />
        <span *ngIf="resolvingDelivery" class="skeleton skeleton-resolved"></span>
        <span *ngIf="deliveryCustomerName" class="customer-badge">{{ deliveryCustomerName }}</span>
        <span *ngIf="!resolvingDelivery && deliveryCustomerName === ''" class="customer-badge customer-badge-missing">
          <i class="fas fa-exclamation-circle"></i>{{ 'AI_PARSER_CONFIG_CUSTOMER_NOT_FOUND' | translate }}
        </span>
      </div>

      <div class="form-field">
        <label class="field-label" for="config-billing">{{ 'AI_PARSER_CONFIG_BILLING_CUSTOMER' | translate }}</label>
        <input
          id="config-billing"
          type="text"
          class="form-control"
          [(ngModel)]="billingCustomerId"
          name="billingCustomerId"
          (ngModelChange)="onCustomerIdChange('billing')"
          [ngbTypeahead]="searchCustomersByName"
          [resultFormatter]="customerSearchFormatter"
          [inputFormatter]="customerIdFormatter"
          (selectItem)="onCustomerPicked('billing', $event)"
          autocomplete="off" />
        <span *ngIf="resolvingBilling" class="skeleton skeleton-resolved"></span>
        <span *ngIf="billingCustomerName" class="customer-badge">{{ billingCustomerName }}</span>
        <span *ngIf="!resolvingBilling && billingCustomerName === ''" class="customer-badge customer-badge-missing">
          <i class="fas fa-exclamation-circle"></i>{{ 'AI_PARSER_CONFIG_CUSTOMER_NOT_FOUND' | translate }}
        </span>
      </div>
    </div>

    <div class="form-field">
      <div class="auto-submit-row">
        <input
          class="pill-switch"
          type="checkbox"
          role="switch"
          id="aiDecidesDeliveryLocation"
          name="aiDecidesDeliveryLocation"
          [(ngModel)]="aiDecidesDeliveryLocation"
        />
        <label class="auto-submit-label" for="aiDecidesDeliveryLocation">
          {{ 'AI_PARSER_CONFIG_AI_DELIVERY_LOCATION' | translate }}
        </label>
      </div>
      <small class="field-hint auto-submit-help">{{ 'AI_PARSER_CONFIG_AI_DELIVERY_LOCATION_HELP' | translate }}</small>
    </div>

    <div class="edit-actions">
      <button type="button" class="btn btn-link text-button" (click)="cancel()" [disabled]="saving">
        {{ 'CANCEL' | translate }}
      </button>
      <button type="submit" class="btn btn-primary" [disabled]="saving">
        {{ 'SAVE' | translate }}
      </button>
    </div>
  </form>
</div>
